// @ts-check
import {geohashForLocation} from 'geofire-common';

import {getURL, getImageByURL} from '../../CloudStorageService.js';
import {ImageGraphic} from './ImageGraphic.js';
import {gmAPI, MapService} from '@/services/MapService';

/**
 * @typedef {import('./ImageGraphic').ImageData} ImageData
 * @typedef {import('../GraphicService').GraphicData & ImageData} ImageGraphicData
 * @typedef {import('@/services/OverlaysService/Feature.js').FeatureData} FeatureData
 */

/**
 * Asynchronously constructs a new ImageGraphic object.
 * @async
 * @param {ImageGraphicData} data The data from which to construct the graphic.
 * @return {Promise<ImageGraphic>} The constructed graphic.
 */
async function createImageGraphic(data) {
  const image = data.image ?? await downloadImage(data.imagePath);
  return new ImageGraphic({...data, image});
}

/**
 * Helper function to download images from Google Cloud Storage.
 * @private
 * @param {string} imagePath The path to an image file on Google Cloud Storage
 * @return A base64 representation of the image.
 */
async function downloadImage(imagePath) {
  return await getImageByURL(await getURL(imagePath));
}

// Dimensions for the block plan images are only given for the interior of the building in feet and inches.
// The dimension we need is the width and height of the entire image in decimal feet.
// The width and height of the entire image can be calculated by the ratio of total to interior pixels times the
// interior dimensions.
// for example, imageXFeet = interiorXFeet * (imageXPixels / interiorXPixels)

const blockPlanImages = new Map([
  // Data Centers
  ['8pod', {
    filePath: 'files/blockPlan_DC8pod_111424.png',
    defaultName: '8 Pod (48MW)',
    widthFeet: 1064.618667,
    heightFeet: 537.79525,
  }],
  ['12pod', {
    filePath: 'files/blockPlan_DC12pod_05062025.png',
    defaultName: '12 Pod (62MW)',
    widthFeet: 1433.753,
    heightFeet: 569.128,
  }],
  // Solar
  ['rooftopArray', {
    filePath: 'files/blockPlan_SolarRooftop_05022025.png',
    defaultName: 'Rooftop Array (500 kW)',
    widthFeet: 220,
    heightFeet: 200,
  }],
]);

/**
 * @param {string} type
 * @param {Function} callback
 */
function createNewImageBlockPlan(type, callback) {
  const image = blockPlanImages.get(type);
  if (!image) {
    throw new Error(`Invalid block plan image type: ${type}`);
  }
  const widthPixels = MapService.feetToHorizontalPixels(image.widthFeet);
  const heightPixels = MapService.feetToVerticalPixels(image.heightFeet);
  const center = MapService.getMap().getCenter();
  const pixelCenter = MapService.latLngToScreenPixel(center);
  const northPixel = pixelCenter.y + heightPixels / 2;
  const eastPixel = pixelCenter.x + widthPixels / 2;
  const southPixel = pixelCenter.y - heightPixels / 2;
  const westPixel = pixelCenter.x - widthPixels / 2;
  const northWest = MapService.screenPixelToLatLng(westPixel, northPixel, true);
  const southEast = MapService.screenPixelToLatLng(eastPixel, southPixel, true);

  const bounds = new gmAPI.LatLngBounds(northWest, southEast);
  const geohash = geohashForLocation([bounds.getCenter().lat(), bounds.getCenter().lng()]);
  const featureData = {
    bounds: bounds.toJSON(),
    geohash,
    displayFields: [],
    name: image.defaultName,
    /** @type {ImageGraphicData} */
    graphicData: {
      featureType: 'png',
      bounds: bounds.toJSON(),
      imagePath: blockPlanImages.get(type)?.filePath,
      preventScaling: true,
      rotation: 0,
    },
  };

  callback(featureData);
}

export {
  createImageGraphic,
  createNewImageBlockPlan,
};
